import { useState, useEffect } from 'react';
import { deviceOperations } from '../services/deviceRouter';
import { getAbsoluteImageUrl } from '../utils/imageUrl';

// Module-level photo cache: `${deviceId}:${photoPath}` → resolved URL or base64 data URL
// Persists for the session; cleared via clearDevicePhotoCache()
const photoFetchCache = new Map<string, string>();
// In-progress fetches: prevents duplicate requests for the same photo
const pendingFetches = new Map<string, Promise<string>>();

/** Clear all cached device photos (call on manual refresh) */
export function clearDevicePhotoCache(deviceId?: string): void {
  if (deviceId) {
    Array.from(photoFetchCache.keys()).forEach(key => {
      if (key.startsWith(`${deviceId}:`)) photoFetchCache.delete(key);
    });
  } else {
    photoFetchCache.clear();
  }
}

/**
 * Device context for photo loading
 */
export interface DevicePhotoContext {
  local_ip_address?: string;
  ip_address?: string;
  username?: string;
  password?: string;
  port?: number;
}

/**
 * React hook for smart photo loading from devices
 *
 * Features:
 * - Tries local IP first (if running in Electron and local IP available)
 * - Falls back to cloud API on timeout/error
 * - Automatic retry on device context changes
 * - Cleanup on unmount
 *
 * @param deviceId Device ID (e.g., "hikvision-123")
 * @param photoPath Photo path/endpoint (e.g., "/user_get_image.fcgi?user_id=123")
 * @param device Device context with IP addresses and credentials
 * @returns { photoUrl, isLoading, error } state
 *
 * @example
 * const { photoUrl, isLoading, error } = useDevicePhoto(
 *   device.id,
 *   `/user_get_image.fcgi?user_id=${person.employeeNo}`,
 *   device
 * );
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <div>Error: {error}</div>;
 * return <img src={photoUrl || ''} alt="Device photo" />;
 */
export function useDevicePhoto(
  deviceId: string | undefined,
  photoPath: string | undefined,
  device?: DevicePhotoContext
): {
  photoUrl: string | null;
  isLoading: boolean;
  error: string | null;
} {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Early return if missing required parameters
    if (!deviceId || !photoPath) {
      setPhotoUrl(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    let isMounted = true;
    let objectUrl: string | null = null;

    const cacheKey = `${deviceId}:${photoPath}`;

    // Return cached result immediately (no loading state)
    const cached = photoFetchCache.get(cacheKey);
    if (cached) {
      setPhotoUrl(cached);
      return;
    }

    const fetchPhoto = async () => {
      setIsLoading(true);
      setError(null);

      // Deduplicate: if a fetch is already in progress for this key, await it
      if (pendingFetches.has(cacheKey)) {
        try {
          const result = await pendingFetches.get(cacheKey)!;
          if (isMounted) {
            setPhotoUrl(result);
            setIsLoading(false);
          }
        } catch {
          if (isMounted) setIsLoading(false);
        }
        return;
      }

      const fetchPromise = (async (): Promise<string> => {
        try {
          // Try smart routing (local first, cloud fallback)
          const result = await deviceOperations.fetchPhoto(
            deviceId,
            photoPath,
            {
              localIp: device?.local_ip_address,
              cloudIp: device?.ip_address,
              username: device?.username,
              password: device?.password,
              port: device?.port,
            }
          );

          if (typeof result !== 'string') throw new Error('Invalid photo response format');
          photoFetchCache.set(cacheKey, result);
          return result;
        } catch (err) {
          // Fallback to cloud proxy URL (browser handles its own HTTP cache)
          const relativeUrl = `/api/v1/iot-connect/facial-recognition/devices/${deviceId}/photo-proxy?path=${encodeURIComponent(photoPath)}`;
          const cloudUrl = getAbsoluteImageUrl(relativeUrl);
          photoFetchCache.set(cacheKey, cloudUrl);
          return cloudUrl;
        } finally {
          pendingFetches.delete(cacheKey);
        }
      })();

      pendingFetches.set(cacheKey, fetchPromise);

      try {
        const result = await fetchPromise;
        if (!isMounted) return;
        setPhotoUrl(result);
        setIsLoading(false);
      } catch (err) {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : String(err));
        setIsLoading(false);
      }
    };

    fetchPhoto();

    // Cleanup function
    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [deviceId, photoPath, device?.local_ip_address, device?.ip_address, device?.username, device?.password, device?.port]);

  return { photoUrl, isLoading, error };
}

/**
 * React hook for batch photo loading from devices
 *
 * Optimized for loading multiple photos from the same device
 *
 * @param deviceId Device ID
 * @param photoPaths Array of photo paths
 * @param device Device context
 * @returns { photos: Map<path, url>, isLoading, errors: Map<path, error> }
 *
 * @example
 * const { photos, isLoading } = useDevicePhotoBatch(
 *   device.id,
 *   ['/photo1.jpg', '/photo2.jpg', '/photo3.jpg'],
 *   device
 * );
 *
 * photos.forEach((url, path) => {
 *   console.log(path, url);
 * });
 */
export function useDevicePhotoBatch(
  deviceId: string | undefined,
  photoPaths: string[] | undefined,
  device?: DevicePhotoContext
): {
  photos: Map<string, string>;
  isLoading: boolean;
  errors: Map<string, string>;
} {
  const [photos, setPhotos] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!deviceId || !photoPaths || photoPaths.length === 0) {
      setPhotos(new Map());
      setIsLoading(false);
      setErrors(new Map());
      return;
    }

    let isMounted = true;

    const fetchAllPhotos = async () => {
      setIsLoading(true);
      const newPhotos = new Map<string, string>();
      const newErrors = new Map<string, string>();

      // Fetch all photos in parallel
      await Promise.all(
        photoPaths.map(async (path) => {
          try {
            const result = await deviceOperations.fetchPhoto(
              deviceId,
              path,
              {
                localIp: device?.local_ip_address,
                cloudIp: device?.ip_address,
                username: device?.username,
                password: device?.password,
                port: device?.port,
              }
            );

            if (isMounted && typeof result === 'string') {
              newPhotos.set(path, result);
            }
          } catch (err) {
            if (isMounted) {
              const errorMsg = err instanceof Error ? err.message : String(err);
              newErrors.set(path, errorMsg);

              // Fallback to cloud URL
              try {
                const cloudUrl = getAbsoluteImageUrl(
                  `/api/v1/iot-connect/facial-recognition/devices/${deviceId}/photo-proxy?path=${encodeURIComponent(path)}`
                );
                newPhotos.set(path, cloudUrl);
              } catch {
                // Ignore fallback errors
              }
            }
          }
        })
      );

      if (isMounted) {
        setPhotos(newPhotos);
        setErrors(newErrors);
        setIsLoading(false);
      }
    };

    fetchAllPhotos();

    return () => {
      isMounted = false;
    };
  }, [deviceId, photoPaths, device?.local_ip_address, device?.ip_address, device?.username, device?.password, device?.port]);

  return { photos, isLoading, errors };
}
